import React, { useCallback } from "react";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { ProductFilter } from "../../ProductFilter/ProductFilter";
import { PRODUCT_LIST_UPLOAD_PROPS } from "../../ProductFilter/constants";
import { ConditionSelectFilter } from "../../Condition/ConditionSelectFilter";
import { FilterMode } from "../../Condition/types";
import { FaiFilterSection } from "../FaiFilter/FaiFilterSection";
import { InstallAgeSection } from "../InstallAgeFilter/InstallAgeSection";
import { IntensitySection } from "../IntensityFilter/IntensitySection";
import { ProductFilterValue } from "../../ProductFilter/types";

interface InclusionSectionProps {
  sectionId: string;
  sectionIndex: number;
  condition: FilterMode;
  selectedProductIds: number[];
  canRemove: boolean;
  onConditionChange: (sectionIndex: number, condition: FilterMode) => void;
  onProductSelect: (sectionIndex: number, value: ProductFilterValue) => void;
  onRemoveSection: (sectionIndex: number) => void;
  isInsideTechInstall?: boolean;
  selectedFaiIds?: string[];
  onFaiSelect?: (
    sectionIndex: number,
    selectedDepartmentIds: string[],
    selectedRoleIds: string[]
  ) => void;
  installAgeMonths?: { min?: number | null; max?: number | null };
  onInstallAgeChange?: (
    sectionIndex: number,
    installAge: { min?: number | null; max?: number | null }
  ) => void;
  intensity?: { min?: number | null; max?: number | null };
  onIntensityChange?: (
    sectionIndex: number,
    intensity: { min?: number | null; max?: number | null }
  ) => void;
}

export const InclusionSection: React.FC<InclusionSectionProps> = React.memo(
  ({
    sectionId,
    sectionIndex,
    condition,
    selectedProductIds,
    canRemove,
    onConditionChange,
    onProductSelect,
    onRemoveSection,
    selectedFaiIds = [],
    onFaiSelect,
    installAgeMonths,
    onInstallAgeChange,
    intensity,
    onIntensityChange,
  }) => {
    const handleFaiSelect = useCallback(
      (selectedDepartmentIds: string[], selectedRoleIds: string[]) => {
        if (onFaiSelect) {
          onFaiSelect(sectionIndex, selectedDepartmentIds, selectedRoleIds);
        }
      },
      [onFaiSelect, sectionIndex]
    );

    const handleInstallAgeChange = useCallback(
      (installAge: { min?: number | null; max?: number | null }) => {
        if (onInstallAgeChange) {
          onInstallAgeChange(sectionIndex, installAge);
        }
      },
      [onInstallAgeChange, sectionIndex]
    );

    const handleIntensityChange = useCallback(
      (next: { min?: number | null; max?: number | null }) => {
        if (onIntensityChange) {
          onIntensityChange(sectionIndex, next);
        }
      },
      [onIntensityChange, sectionIndex]
    );
    return (
      <Paper
        key={sectionId}
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body1">If</Typography>
            <ConditionSelectFilter
              id={`product-condition-${sectionIndex}`}
              value={condition}
              onChange={(value) => onConditionChange(sectionIndex, value)}
            />
            <Typography variant="body1">
              of the following products are installed
            </Typography>
          </Stack>
          {canRemove && (
            <IconButton
              size="small"
              onClick={() => onRemoveSection(sectionIndex)}
              aria-label="Remove section"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <ProductFilter
          onProductsSelect={(value) => onProductSelect(sectionIndex, value)}
          selectedProductIds={selectedProductIds}
          uploadProps={PRODUCT_LIST_UPLOAD_PROPS}
        />

        <FaiFilterSection
          disabled={selectedProductIds.length === 0}
          selectedIds={selectedFaiIds}
          onFaiSelect={handleFaiSelect}
          id={`fai-filter-${sectionId}`}
        />

        <InstallAgeSection
          disabled={selectedProductIds.length === 0}
          value={installAgeMonths || { min: undefined, max: undefined }}
          onChange={handleInstallAgeChange}
          filterHasSelection={selectedProductIds.length > 0}
        />

        <IntensitySection
          disabled={selectedProductIds.length === 0}
          value={intensity || { min: undefined, max: undefined }}
          onChange={handleIntensityChange}
          filterHasSelection={selectedProductIds.length > 0}
        />
      </Paper>
    );
  }
);
